import { Order } from 'effect'
import type { NonEmptyArray } from 'effect/Array'
import type { RaceSettledEvent, RaceStartedEvent } from 'src/types/domain/RaceEvent'
import type { LightningBoostType } from 'src/types/domain/Race'
import type { RoundOdds } from 'src/types/domain/RoundOdds'
import type { HardClose } from 'src/types/domain/HardClose'

export interface NormalizedMarbleGame {
  type: 'MarbleGame'
  marbleIds: string[]
  id: string
  trackId: string
  name: string
  laps: number
  totalMarbles: number
  maxPickableMarbles: number
  bettingOpenWindow: number
  bettingClosingWindow: number
  settledWindow: number
  resultsToSubmit: number
  active: boolean
  previewThumbnail?: string
  broadcastIds?: string[]
  raceIds: NonEmptyArray<string>
  createdAt: string
}

export interface NormalizedRace {
  id: string
  round: number
  createdAt: string
  raceConfigurationId: string
  raceEventIds: string[]
  isLightningRound: boolean
  lightningBoostType: LightningBoostType | null
  lightningBoostPercent: number | null
  lightningBoostedItems: string[] | null
}

export const orderRacesByCreatedAt = Order.mapInput(
  Order.reverse(Order.string),
  (race: NormalizedRace) => race.createdAt
)

export interface NormalizedRaceEvent {
  uuid: string
  raceId: string
  eventType: string
  eventTimestamp: string
}

export interface NormalizedRaceSettledEvent extends NormalizedRaceEvent {
  eventType: 'race_settled'
  payload: {
    results: {
      marbleId: string
      marbleNumber: number
      marbleColor: string
      position: number
    }[]
  }
}

export interface NormalizedRaceScheduledEvent extends NormalizedRaceEvent {
  eventType: 'race_scheduled'
  payload: {
    start_timestamp: string
  }
}

export interface NormalizedRaceEndedEvent extends NormalizedRaceEvent {
  eventType: 'race_ended'
  payload?: {
    crash_multiplier?: number
  }
}

// CCTV games attach `boundaries` + `optionLabels` to the
// race_started event so the chip-placement panel can render
// per-option boundary labels once betting closes. Without this
// normalized variant + a corresponding mapper branch, the store
// strips the payload (the default branch at the bottom of
// `toNormalizedRaceEvent` only carries the four base fields)
// and `currentRace.raceEvents.find(...)` returns an event
// without payload — even when the websocket+zod parsing
// successfully delivered the data.
// Birdie adds `location` + `venue` on the same event, so the normalized
// variant tracks the domain payload rather than restating a CCTV-shaped
// subset — a narrower type here would silently drop the Birdie keys the
// mapper does carry.
export interface NormalizedRaceStartedEvent extends NormalizedRaceEvent {
  eventType: 'race_started'
  payload?: RaceStartedEvent['payload']
}

// race_ready carries the crash provable-fairness commitment
// (`serverSeedHash`). Like the race_started CCTV payload above, the
// store's default normalization branch strips unknown payloads — so
// this variant + a matching mapper branch is required to keep the
// commitment readable via `currentRace.raceEvents.find(...)`.
export interface NormalizedRaceReadyEvent extends NormalizedRaceEvent {
  eventType: 'race_ready'
  payload?: {
    serverSeedHash?: string
    // Dynamic-odds games (Birdie): the round's committed board.
    odds?: RoundOdds
    // The hard close: present only on a round whose engine sent it hard.
    hardClose?: HardClose
  }
}

// race_crashed is the SYNTHETIC event built from the `crash:occurred`
// broadcast (Go auto_cashout_scheduler) — the only carrier of the crash
// multiplier at the visual-crash instant (race_ended trails it). Like the
// race_started / race_ready payloads above, the store's default
// normalization branch strips unknown payloads — without this variant +
// mapper branch the hero stamp had no M at crash time and fell through to
// its 1.00x floor (the "1.00x → 2.12x" stamp flash).
export interface NormalizedRaceCrashedEvent extends NormalizedRaceEvent {
  eventType: 'race_crashed'
  payload?: {
    crashMultiplier?: number
  }
}

export const isNormalizedRaceSettledEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceSettledEvent => raceEvent.eventType === 'race_settled'

export const isNormalizedRaceScheduledEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceScheduledEvent => raceEvent.eventType === 'race_scheduled'

export const isNormalizedRaceEndedEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceEndedEvent => raceEvent.eventType === 'race_ended'

export const isNormalizedRaceStartedEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceStartedEvent => raceEvent.eventType === 'race_started'

export const isNormalizedRaceReadyEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceReadyEvent => raceEvent.eventType === 'race_ready'

export const isNormalizedRaceCrashedEvent = (
  raceEvent: NormalizedRaceEvent
): raceEvent is NormalizedRaceCrashedEvent => raceEvent.eventType === 'race_crashed'

export const orderRaceEventsByTimestamp = Order.mapInput(
  Order.string,
  (raceEvent: NormalizedRaceEvent) => raceEvent.eventTimestamp
)

export const orderResultsByPosition = Order.mapInput(
  Order.number,
  (result: RaceSettledEvent['payload']['results'][number]) => result.position
)

export interface NormalizedMarble {
  uuid: string
  color: string
  weight: string
  picture: string | null
  image: string | null
  imageAlt: string | null
  rfidId: string | null
  number: string
  active: boolean
  characteristics: string | null
  raceConfigurationId: string | null
  createdAt: string
}

export interface NormalizedTrack {
  uuid: string
  active: boolean
  showInLobby: boolean
  createdAt: string
  description: string | null
  image: string
  poster: string
  background: string
  videoPreviewMp4: string | null
  videoPreviewWebm: string | null
  name: string
  gameType: string
  shortName: string
  modeId: string
}

export interface NormalizedTrackMode {
  uuid: string
  name: string
  type: string
  trackId: string
  active: boolean
}
